import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';


const RAW_BASE = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  ''
).trim();

const BASE_NO_SLASH = RAW_BASE.replace(/\/+$/, '');

const API = BASE_NO_SLASH
  ? BASE_NO_SLASH.endsWith('/api')
    ? BASE_NO_SLASH
    : `${BASE_NO_SLASH}/api`
  : '/api';


async function request(
  url,
  {
    method = 'GET',
    body,
    headers,
  } = {}
) {
  const options = {
    method,
    headers: {
      ...(headers || {}),
    },
  };

  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);
  const text = await response.text();

  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = text;
  }

  if (!response.ok) {
    let detail = 'Request failed';

    if (data && typeof data === 'object') {
      detail =
        data.detail ||
        data.non_field_errors ||
        data;
    } else if (typeof data === 'string' && data) {
      detail = data;
    } else if (response.statusText) {
      detail = response.statusText;
    }

    const error = new Error(
      typeof detail === 'string'
        ? detail
        : JSON.stringify(detail)
    );

    error.response = {
      status: response.status,
      data,
    };

    throw error;
  }

  return data;
}


function formatDate(dateString) {
  if (!dateString) {
    return 'Not available';
  }

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}


function FieldError({ children }) {
  if (!children) {
    return null;
  }

  return (
    <p className="mt-1 text-sm text-red-600">
      {children}
    </p>
  );
}


function LoadingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-4">
        <div className="text-center">
          <svg
            className="mx-auto h-8 w-8 animate-spin text-indigo-600"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />

            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
            />
          </svg>

          <p className="mt-4 text-sm text-gray-600">
            Loading your case information...
          </p>
        </div>
      </div>
    </div>
  );
}


function ErrorPage({ message }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Cisowski Law
            </div>

            <div className="text-xs text-gray-500">
              Bankruptcy Representation
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 ring-1 ring-inset ring-red-100">
            <svg
              className="h-7 w-7 text-red-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-1.875c0 5.592-4.03 10.235-9.334 11.19a.9.9 0 0 1-.332 0C6.03 21.11 2 16.467 2 10.875V5.989c0-.836.51-1.587 1.287-1.899l8.25-3.3a1.25 1.25 0 0 1 .926 0l8.25 3.3A2.043 2.043 0 0 1 22 5.989v4.886Z"
              />
            </svg>
          </div>

          <h1 className="mt-6 text-2xl font-semibold text-gray-900">
            Case page unavailable
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-600">
            {message}
          </p>

          <a
            href="/substitution-counsel"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Return to case search
          </a>
        </div>
      </main>
    </div>
  );
}


function SuccessPage({ caseData }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Cisowski Law
            </div>

            <div className="text-xs text-gray-500">
              Bankruptcy Representation
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200 sm:p-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 ring-1 ring-inset ring-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          </div>

          <p className="mt-6 text-sm font-semibold uppercase tracking-wide text-green-700">
            Request received
          </p>

          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
            Thank you
          </h1>

          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-gray-600">
            Your consultation request concerning Chapter 13 case{' '}
            <span className="font-semibold text-gray-900">
              {caseData?.case_number}
            </span>{' '}
            has been submitted. A member of our office will review your
            information and contact you.
          </p>

          <div className="mt-8 rounded-lg bg-amber-50 p-4 text-left ring-1 ring-inset ring-amber-200">
            <p className="text-sm leading-6 text-amber-900">
              Submitting this request does not create an attorney-client
              relationship. Do not assume that Cisowski Law represents you
              unless a written representation agreement has been signed.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}


export default function SubstitutionCounselCasePage() {
  const { publicToken } = useParams();

  const [caseData, setCaseData] = useState(null);
  const [loadingCase, setLoadingCase] = useState(true);
  const [caseError, setCaseError] = useState('');

  const [form, setForm] = useState({
    email: '',
    phone: '',
    preferred_contact_method: 'either',
    best_time_to_contact: '',
    message: '',
    consent_to_contact: false,
    confirms_debtor: false,
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  useEffect(() => {
    const fetchCase = async () => {
      setLoadingCase(true);
      setCaseError('');

      try {
        const data = await request(
          `${API}/substitution-counsel/case/${publicToken}/`
        );

        setCaseData(data);
      } catch (error) {
        setCaseError(
          error?.message ||
          'This case page could not be found or is no longer available.'
        );
      } finally {
        setLoadingCase(false);
      }
    };

    if (!publicToken) {
      setCaseError('No case identifier was provided.');
      setLoadingCase(false);
      return;
    }

    fetchCase();
  }, [publicToken]);


  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [field]: '',
    }));

    setSubmitError('');
  };


  const validateForm = () => {
    const errors = {};

    if (!form.email.trim()) {
      errors.email = 'Please enter your email address.';
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      errors.email = 'Please enter a valid email address.';
    }

    const phoneDigits = form.phone.replace(/\D/g, '');

    if (!form.phone.trim()) {
      errors.phone = 'Please enter your phone number.';
    } else if (phoneDigits.length < 10) {
      errors.phone = 'Please enter a valid phone number.';
    }

    if (
      !['phone', 'email', 'either'].includes(
        form.preferred_contact_method
      )
    ) {
      errors.preferred_contact_method =
        'Please select a preferred contact method.';
    }

    if (!form.confirms_debtor) {
      errors.confirms_debtor =
        'Please confirm that you are a debtor in this case or are authorized to submit this request.';
    }

    if (!form.consent_to_contact) {
      errors.consent_to_contact =
        'Please consent to being contacted by our office.';
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      await request(
        `${API}/substitution-counsel/case/${publicToken}/consultation/`,
        {
          method: 'POST',
          body: {
            email: form.email.trim(),
            phone: form.phone.trim(),
            preferred_contact_method:
              form.preferred_contact_method,
            best_time_to_contact:
              form.best_time_to_contact.trim(),
            message: form.message.trim(),
            consent_to_contact:
              form.consent_to_contact,
            confirms_debtor:
              form.confirms_debtor,
          },
        }
      );

      setSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } catch (error) {
      const responseData = error?.response?.data;

      if (
        responseData &&
        typeof responseData === 'object' &&
        !Array.isArray(responseData)
      ) {
        const backendErrors = {};

        Object.entries(responseData).forEach(
          ([field, value]) => {
            if (field === 'detail') {
              return;
            }

            backendErrors[field] = Array.isArray(value)
              ? value[0]
              : String(value);
          }
        );

        if (Object.keys(backendErrors).length > 0) {
          setFieldErrors(backendErrors);
        }
      }

      setSubmitError(
        responseData?.detail ||
        error?.message ||
        'Your request could not be submitted. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };


  if (loadingCase) {
    return <LoadingPage />;
  }

  if (caseError || !caseData) {
    return (
      <ErrorPage
        message={
          caseError ||
          'This case page could not be found or is no longer available.'
        }
      />
    );
  }

  if (submitted) {
    return <SuccessPage caseData={caseData} />;
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Cisowski Law
            </div>

            <div className="text-xs text-gray-500">
              Bankruptcy Representation
            </div>
          </div>

          <div className="hidden text-right sm:block">
            <div className="text-sm font-medium text-gray-900">
              Chapter 13 Assistance
            </div>

            <div className="text-xs text-gray-500">
              Consultation request
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600">
                Verified Chapter 13 case
              </p>

              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                Request a consultation
              </h1>

              <p className="mt-4 max-w-3xl text-base leading-7 text-gray-600">
                Review the case information below. Then provide your
                contact information to request a consultation concerning
                possible substitution of counsel.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">

            <div>
              <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
                <div className="border-b border-gray-200 px-6 py-5">
                  <p className="text-sm font-semibold text-indigo-600">
                    Case information
                  </p>

                  <h2 className="mt-1 text-xl font-semibold text-gray-900">
                    {caseData.case_title}
                  </h2>
                </div>

                <dl className="divide-y divide-gray-100 px-6">
                  <div className="py-4">
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Case number
                    </dt>

                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {caseData.case_number}
                    </dd>
                  </div>

                  <div className="py-4">
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Chapter
                    </dt>

                    <dd className="mt-1 text-sm text-gray-900">
                      Chapter {caseData.chapter}
                    </dd>
                  </div>

                  <div className="py-4">
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Date filed
                    </dt>

                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(caseData.date_filed)}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="mt-6 rounded-xl bg-amber-50 p-5 ring-1 ring-inset ring-amber-200">
                <h3 className="text-sm font-semibold text-amber-900">
                  Important
                </h3>

                <p className="mt-2 text-sm leading-6 text-amber-900">
                  A consultation request does not guarantee that Cisowski
                  Law will accept your case. Continue complying with all
                  court orders, trustee requirements, payment obligations,
                  and deadlines unless you receive different legal advice.
                </p>
              </div>
            </div>

            <div>
              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-8">
                <div>
                  <p className="text-sm font-semibold text-indigo-600">
                    Contact information
                  </p>

                  <h2 className="mt-2 text-2xl font-semibold text-gray-900">
                    How can we reach you?
                  </h2>

                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Please provide current contact information. A member
                    of our office will use this information to follow up
                    about a consultation.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="mt-8 space-y-6"
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email address
                      </label>

                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(event) =>
                          updateForm('email', event.target.value)
                        }
                        disabled={submitting}
                        className={`mt-1 block w-full rounded-md border bg-white px-3 py-2.5 text-gray-900 shadow-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                          fieldErrors.email
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                        }`}
                      />

                      <FieldError>
                        {fieldErrors.email}
                      </FieldError>
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Phone number
                      </label>

                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(event) =>
                          updateForm('phone', event.target.value)
                        }
                        placeholder="423-555-1234"
                        disabled={submitting}
                        className={`mt-1 block w-full rounded-md border bg-white px-3 py-2.5 text-gray-900 shadow-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                          fieldErrors.phone
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                        }`}
                      />

                      <FieldError>
                        {fieldErrors.phone}
                      </FieldError>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="preferred-contact-method"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Preferred contact method
                    </label>

                    <select
                      id="preferred-contact-method"
                      name="preferred_contact_method"
                      value={form.preferred_contact_method}
                      onChange={(event) =>
                        updateForm(
                          'preferred_contact_method',
                          event.target.value
                        )
                      }
                      disabled={submitting}
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                    >
                      <option value="either">
                        Phone or email
                      </option>

                      <option value="phone">
                        Phone
                      </option>

                      <option value="email">
                        Email
                      </option>
                    </select>

                    <FieldError>
                      {fieldErrors.preferred_contact_method}
                    </FieldError>
                  </div>

                  <div>
                    <label
                      htmlFor="best-time"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Best time to contact you
                      <span className="ml-1 font-normal text-gray-500">
                        Optional
                      </span>
                    </label>

                    <input
                      id="best-time"
                      name="best_time_to_contact"
                      type="text"
                      value={form.best_time_to_contact}
                      onChange={(event) =>
                        updateForm(
                          'best_time_to_contact',
                          event.target.value
                        )
                      }
                      placeholder="Example: Weekdays after 3:00 PM"
                      maxLength={100}
                      disabled={submitting}
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Message
                      <span className="ml-1 font-normal text-gray-500">
                        Optional
                      </span>
                    </label>

                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={(event) =>
                        updateForm('message', event.target.value)
                      }
                      placeholder="Provide any information you would like our office to know before contacting you."
                      disabled={submitting}
                      className="mt-1 block w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
                        <input
                          type="checkbox"
                          checked={form.confirms_debtor}
                          onChange={(event) =>
                            updateForm(
                              'confirms_debtor',
                              event.target.checked
                            )
                          }
                          disabled={submitting}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />

                        <span className="text-sm leading-6 text-gray-700">
                          I confirm that I am a debtor in this bankruptcy
                          case or that I am authorized to submit this
                          request on the debtor’s behalf.
                        </span>
                      </label>

                      <FieldError>
                        {fieldErrors.confirms_debtor}
                      </FieldError>
                    </div>

                    <div>
                      <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
                        <input
                          type="checkbox"
                          checked={form.consent_to_contact}
                          onChange={(event) =>
                            updateForm(
                              'consent_to_contact',
                              event.target.checked
                            )
                          }
                          disabled={submitting}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />

                        <span className="text-sm leading-6 text-gray-700">
                          I authorize Cisowski Law to contact me by phone,
                          text message, or email regarding this consultation
                          request.
                        </span>
                      </label>

                      <FieldError>
                        {fieldErrors.consent_to_contact}
                      </FieldError>
                    </div>
                  </div>

                  {submitError && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 p-4 text-sm text-red-700 ring-1 ring-inset ring-red-200"
                    >
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="-ml-1 mr-2 h-5 w-5 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />

                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
                          />
                        </svg>

                        Submitting request...
                      </>
                    ) : (
                      'Request consultation'
                    )}
                  </button>
                </form>

                <p className="mt-5 text-xs leading-5 text-gray-500">
                  Submitting this request does not create an
                  attorney-client relationship. Representation begins
                  only after a written agreement is signed by you and
                  Cisowski Law.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 text-center sm:px-6 lg:px-8">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Cisowski Law. All rights
            reserved.
          </p>

          <p className="mx-auto mt-3 max-w-3xl text-xs leading-5 text-gray-500">
            This website provides general information and a way to
            request a consultation. It does not provide legal advice and
            does not create an attorney-client relationship.
          </p>
        </div>
      </footer>
    </div>
  );
}